/**
 * "Email me this plan" — the loss-prevention capture at the review step.
 *
 * Five of twelve shoppers loop on the same route and abandon at review
 * repeatedly (Ezra, 2026-09-07). They are comparing, not confused. This gives
 * them the exact numbers on screen in their inbox — deposit, installment
 * amount and count, trip total — with a one-tap link that resumes THIS cart
 * (server/cart-recovery.js signs the link; Review.jsx honours `?resume=`).
 *
 * Signed-in customers get it with one tap; signed-out ones type an email.
 * The server owns the send (Resend, "Trip reminders" topic, unsubscribe in
 * the footer) and records it on cart_recovery_emails_sent, which also lets
 * the hourly recovery cron reuse the same snapshot later.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Mail, Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/activityTracker';
import { requestPlanEmail } from '@/lib/checkoutPlanEmail';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @param {object} props
 * @param {string|null} props.cartId          abandoned_trips.id (null until the cart exists)
 * @param {string|null} props.sessionId       booking-flow session id (ownership proof when signed out)
 * @param {string|null} props.knownEmail      signed-in customer's email, if any
 * @param {() => object} props.buildSnapshot  returns the plan the customer is looking at right now
 */
export default function EmailMyPlan({ cartId, sessionId, knownEmail, buildSnapshot, className }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | editing | sending | sent | error
  const [error, setError] = useState(null);

  if (!cartId) return null;

  const send = async (to) => {
    setState('sending');
    setError(null);
    try {
      await requestPlanEmail({ cartId, sessionId, email: to, snapshot: buildSnapshot() });
      trackEvent('checkout_plan_email_requested', { signed_in: !!knownEmail });
      setState('sent');
    } catch (e) {
      setError(e.message || 'Could not send the email');
      setState('error');
    }
  };

  if (state === 'sent') {
    return (
      <p className={cn('inline-flex items-center gap-1.5 text-[13px] font-normal text-emerald-300/90', className)}>
        <Check className="h-4 w-4" aria-hidden />
        Sent — check your inbox for this plan.
      </p>
    );
  }

  if (knownEmail) {
    return (
      <div className={cn('flex flex-col items-center gap-1', className)}>
        <button
          type="button"
          onClick={() => send(null)}
          disabled={state === 'sending'}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-[13px] font-normal text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors disabled:opacity-60"
        >
          {state === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Mail className="h-4 w-4" aria-hidden />}
          Email me this plan
        </button>
        {error && <span className="text-[12px] text-red-300/90">{error}</span>}
      </div>
    );
  }

  if (state === 'idle') {
    return (
      <button
        type="button"
        onClick={() => setState('editing')}
        className={cn('inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-[13px] font-normal text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors', className)}
      >
        <Mail className="h-4 w-4" aria-hidden />
        Email me this plan
      </button>
    );
  }

  const valid = EMAIL_RE.test(email.trim());
  return (
    <form
      className={cn('flex w-full max-w-sm flex-col gap-2', className)}
      onSubmit={(e) => { e.preventDefault(); if (valid) send(email.trim()); }}
    >
      <label htmlFor="email-my-plan" className="text-[12px] font-normal text-white/55">Where should we send this plan?</label>
      <div className="flex gap-2">
        <input
          id="email-my-plan"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="min-w-0 flex-1 rounded-full border border-white/15 bg-white/[0.05] px-4 py-2 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/35"
        />
        <button
          type="submit"
          disabled={!valid || state === 'sending'}
          className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black disabled:opacity-50"
        >
          {state === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Mail className="h-4 w-4" aria-hidden />}
          Send
        </button>
      </div>
      {error && <span className="text-[12px] text-red-300/90">{error}</span>}
      <span className="text-[11px] font-normal text-white/35">One email with these numbers and a link back to this booking. Unsubscribe any time.</span>
    </form>
  );
}

EmailMyPlan.propTypes = {
  cartId: PropTypes.string,
  sessionId: PropTypes.string,
  knownEmail: PropTypes.string,
  buildSnapshot: PropTypes.func.isRequired,
  className: PropTypes.string,
};
